import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CustomerAuthProvider, useCustomerAuth } from "./context/CustomerAuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { ConfirmProvider } from "./components/ui/confirm-dialog";
import { Layout } from "./components/layout/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { Home } from "./pages/Home";

// Lazy-loaded pages — only downloaded when the user navigates to them
const BlogList = lazy(() => import("./pages/Blog").then(m => ({ default: m.BlogList })));
const BlogPost = lazy(() => import("./pages/Blog").then(m => ({ default: m.BlogPost })));
const Gallery = lazy(() => import("./pages/Gallery").then(m => ({ default: m.Gallery })));
const ShrimpRoom = lazy(() => import("./pages/ShrimpRoom").then(m => ({ default: m.ShrimpRoom })));
const Equipment = lazy(() => import("./pages/Equipment").then(m => ({ default: m.Equipment })));
const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions").then(m => ({ default: m.TermsAndConditions })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const Login = lazy(() => import("./pages/Login").then(m => ({ default: m.Login })));
const AdminDashboard = lazy(() => import("./pages/Admin").then(m => ({ default: m.AdminDashboard })));
const Checkout = lazy(() => import("./pages/Checkout").then(m => ({ default: m.Checkout })));
const CustomerRegister = lazy(() => import("./pages/CustomerRegister").then(m => ({ default: m.CustomerRegister })));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin").then(m => ({ default: m.CustomerLogin })));
const MyAccount = lazy(() => import("./pages/MyAccount").then(m => ({ default: m.MyAccount })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess").then(m => ({ default: m.CheckoutSuccess })));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel").then(m => ({ default: m.CheckoutCancel })));
import NotFound from "./pages/NotFound";
import { NoIndex } from "./components/SEO";

const TrackOrder = lazy(() => import("./pages/TrackOrder").then(m => ({ default: m.TrackOrder })));
const Recommendations = lazy(() => import("./pages/Recommendations").then(m => ({ default: m.Recommendations })));
const Updates = lazy(() => import("./pages/Updates").then(m => ({ default: m.Updates })));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ReviewSubmit = lazy(() => import("./pages/ReviewSubmit"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-shrimp-bg">
    <div className="spinner"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-shrimp-bg">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route - redirect to admin if logged in
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-shrimp-bg">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Customer Protected Route
const CustomerProtectedRoute = ({ children }) => {
  const { customer, loading } = useCustomerAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-shrimp-bg">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!customer) {
    return <Navigate to="/customer-login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/blog" element={<Layout><BlogList /></Layout>} />
      <Route path="/blog/:id" element={<Layout><BlogPost /></Layout>} />
      <Route path="/gallery" element={<Layout><Gallery /></Layout>} />
      <Route path="/shrimp-room" element={<Layout><ShrimpRoom /></Layout>} />
      <Route path="/product/:slug" element={<Layout><ProductPage /></Layout>} />
      <Route path="/review/:token" element={<Layout><NoIndex title="Leave a Review" /><ReviewSubmit /></Layout>} />
      <Route path="/equipment" element={<Layout><Equipment /></Layout>} />
      <Route path="/recommendations" element={<Layout><Recommendations /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/updates" element={<Layout><Updates /></Layout>} />
      <Route path="/terms" element={<Layout><TermsAndConditions /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/checkout" element={<Layout><NoIndex title="Checkout" /><Checkout /></Layout>} />
      <Route path="/checkout/success" element={<Layout><NoIndex title="Order Confirmed" /><CheckoutSuccess /></Layout>} />
      <Route path="/checkout/cancel" element={<Layout><NoIndex title="Checkout Cancelled" /><CheckoutCancel /></Layout>} />
      
      {/* Order Tracking - Public */}
      <Route path="/track" element={<Layout><TrackOrder /></Layout>} />
      <Route path="/track/:orderNumber" element={<Layout><TrackOrder /></Layout>} />
      
      {/* Customer Auth Routes (crawlable but noindex) */}
      <Route path="/register" element={<Layout><NoIndex title="Create Account" /><CustomerRegister /></Layout>} />
      <Route path="/customer-login" element={<Layout><NoIndex title="Sign In" /><CustomerLogin /></Layout>} />
      <Route path="/my-account" element={<Layout><NoIndex title="My Account" /><MyAccount /></Layout>} />
      <Route path="/forgot-password" element={<Layout><NoIndex title="Forgot Password" /><ForgotPassword /></Layout>} />
      <Route path="/reset-password" element={<Layout><NoIndex title="Reset Password" /><ResetPassword /></Layout>} />
      <Route path="/unsubscribe" element={<><NoIndex title="Unsubscribe" /><Unsubscribe /></>} />
      
      {/* Admin Auth Routes */}
      <Route path="/login" element={
        <PublicOnlyRoute>
          <Layout><NoIndex title="Staff Login" /><Login /></Layout>
        </PublicOnlyRoute>
      } />
      
      {/* Protected Admin Route */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <NoIndex title="Admin" />
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      {/* 404 — friendly Not Found page (returns the React app shell; useful for both UX and SEO via robots noindex) */}
      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <CustomerAuthProvider>
          <WishlistProvider>
            <CartProvider>
              <ConfirmProvider>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
                <Toaster position="top-right" richColors />
              </ConfirmProvider>
            </CartProvider>
          </WishlistProvider>
        </CustomerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
